// Watermark.tsx - 保证生效版
import React, { useEffect, useRef, useState } from 'react';

interface WatermarkProps {
  text: string;
  children: React.ReactNode;
  opacity?: number;
  fontSize?: number;
  fontColor?: string;
  showWatermark?: boolean;
}

export default function Watermark({
  text,
  children,
  opacity = 0.12,
  fontSize = 16,
  fontColor = '#000',
  showWatermark = true,
}: WatermarkProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pattern, setPattern] = useState('');
  const [isReady, setIsReady] = useState(false);
  const [gapSize, setGapSize] = useState(200);

  // 1. 生成水印图案（带错误处理）
  useEffect(() => {
    if (!showWatermark) {
      return;
    }
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        console.error('浏览器不支持Canvas');
        return;
      }

      // 设置尺寸（根据字体大小自适应）
      const textWidth = text.length * fontSize * 0.6; // 估算文字宽度
      // const gap = Math.max(textWidth * 1.5, 160); // 最小间距150
      const gap = Math.min(320, Math.max(200, Math.ceil(textWidth * 1.6)));
      canvas.width = gap;
      canvas.height = gap;

      // 绘制文字
      ctx.font = `${fontSize}px ABBVoice, Verdana`;
      ctx.save();
      ctx.globalAlpha = opacity;
      ctx.fillStyle = fontColor;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // 旋转画布
      ctx.translate(gap / 2, gap / 2);
      ctx.rotate((-45 * Math.PI) / 180);
      ctx.fillText(text, 0, 0);
      ctx.restore();

      const dataUrl = canvas.toDataURL('image/png');
      setPattern(`url(${dataUrl})`);
      setIsReady(true);
      setGapSize(gap);

      console.log('✅ 水印生成成功:', dataUrl); // 调试日志
    } catch (error) {
      console.error('❌ 水印生成失败:', error);
    }
  }, [text, opacity, fontSize, fontColor, showWatermark]);

  // 2. 确保容器定位正确
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // 自动修复容器定位
    const computedStyle = getComputedStyle(container);
    if (computedStyle.position === 'static') {
      container.style.position = 'relative';
      console.log('⚠️ 已自动修复容器定位');
    }

    // 防止水印被意外删除（防误删机制）
    const observer = new MutationObserver(() => {
      const watermark = container.querySelector('[data-watermark-layer]');
      if (!watermark && isReady) {
        console.log('⚠️ 水印被删除，重新添加');
        // 触发重新渲染
        // window.location.reload();
      }
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style'],
    });

    return () => observer.disconnect();
  }, [isReady]);
  // 3. 渲染水印层
  if (!pattern) {
    return <div ref={containerRef}>{children}</div>;
  }

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative', overflow: 'visible' }}
    >
      {children}

      {/* 水印层 - 关键样式 */}
      {showWatermark && (
        <div
          data-watermark-layer
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none', // 允许点击穿透
            userSelect: 'none', // 禁止选中
            zIndex: 9999, // 确保在最上层
            backgroundImage: pattern,
            backgroundSize: `${gapSize}px ${gapSize}px`, // 与生成时匹配
            backgroundRepeat: 'repeat',
            // 调试样式（正式使用时删除）
            // border: '2px solid red',
            // opacity: 0.5,
          }}
        />
      )}
    </div>
  );
}
